import { __extends, __makeTemplateObject, __decorate, __assign as _assign, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit-element/lit-element.js';
import { html } from '../../node_modules/lit-html/lit-html.js';
import { ifDefined } from '../../node_modules/lit-html/directives/if-defined.js';
import { Slotify } from '../../node_modules/@eightshapes/slotify/src/slotify.js';
import customElement from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/decorators/custom-element.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { StandardSize, Size } from '../pvd-utils/enums/size.js';
import { nothing } from '../../node_modules/lit-html/lib/part.js';
import { ScopedElementsMixin } from '../../node_modules/@open-wc/scoped-elements/src/ScopedElementsMixin.js';
import { property } from '../../node_modules/lit-element/lib/decorators.js';

var ButtonType;
(function (ButtonType) {
    ButtonType["button"] = "button";
    ButtonType["submit"] = "submit";
    ButtonType["reset"] = "reset";
})(ButtonType || (ButtonType = {}));
var ButtonVariant;
(function (ButtonVariant) {
    ButtonVariant["primary"] = "primary";
    ButtonVariant["secondary"] = "secondary";
    ButtonVariant["tertiary"] = "tertiary";
    ButtonVariant["invitation"] = "invitation";
    ButtonVariant["trade"] = "trade";
})(ButtonVariant || (ButtonVariant = {}));
var DEFAULT_DISABLED = false;
var DEFAULT_FULL_WIDTH = false;
var DEFAULT_GRADIENT_BACKGROUND = false;
var DEFAULT_ICON_ONLY = false;
var DEFAULT_SIZE = StandardSize.medium;
var DEFAULT_TYPE = ButtonType.button;
var DEFAULT_VARIANT = ButtonVariant.primary;
var ANCHOR_PROTECTED_ATTR_MAP = new Map([['href', 'pvd-href']]);
var BUTTON_PROTECTED_ATTR_MAP = new Map([
    ['disabled', 'pvd-disabled'],
    ['type', 'pvd-type'],
]);
/**
 * Providence Button component.
 * @see {@link https://providence.fmr.com/components/button/button.html|Button documentation}
 */
var CustomElementMixin = customElement({
    name: 'button',
    namespace: 'pvd',
});
var PvdButton = /** @class */ (function (_super) {
    __extends(PvdButton, _super);
    function PvdButton() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        // #region LitElement properties
        _this.pvdDisabled = DEFAULT_DISABLED;
        _this.pvdFullWidth = DEFAULT_FULL_WIDTH;
        _this.pvdGradientBackground = DEFAULT_GRADIENT_BACKGROUND;
        _this.pvdIconOnly = DEFAULT_ICON_ONLY;
        _this.pvdSize = DEFAULT_SIZE;
        _this.pvdType = DEFAULT_TYPE;
        _this.pvdVariant = DEFAULT_VARIANT;
        return _this;
        // #endregion
    }
    Object.defineProperty(PvdButton, "scopedElements", {
        get: function () {
            // $namespace-$name-$extendingComponentName
            return {
                's-slot': customElements.get('s-slot'),
                // @ts-ignore
                'pvd-scoped-icon': customElements.get(this.namespace + "-icon"),
            };
        },
        enumerable: true,
        configurable: true
    });
    // #endregion
    // #region public methods
    PvdButton.prototype.click = function () {
        // [PVD-10179] prevent click event from triggering when Button is disabled
        if (!this.pvdDisabled) {
            _super.prototype.click.call(this);
        }
    };
    Object.defineProperty(PvdButton.prototype, "hasIcon", {
        //#endregion
        // #region private methods
        get: function () {
            return this.hasIconLeft || this.hasIconRight;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PvdButton.prototype, "hasIconLeft", {
        get: function () {
            return !!this.pvdIconLeft || this.isSlotUsed('icon-left');
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PvdButton.prototype, "hasIconRight", {
        get: function () {
            return !!this.pvdIconRight || this.isSlotUsed('icon-right');
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PvdButton.prototype, "hasText", {
        get: function () {
            return !!this.pvdText || this.isSlotUsed();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Sets attributes on the button element that are coming from pvdNativeButtonAttributes
     * @param prevAttr optional, if user updates pvdNativeButtonAttributes, this will remove/update existing attributes
     */
    PvdButton.prototype.setNativeButtonAttributes = function (prevAttr) {
        var targetElement = this.querySelector('button');
        this.setNativeAttributes(this.pvdNativeButtonAttributes, BUTTON_PROTECTED_ATTR_MAP, targetElement, prevAttr);
    };
    /**
     * Sets attributes on the anchor element that are coming from pvdNativeAnchorAttributes
     * @param prevAttr optional, if user updates pvdNativeAnchorAttributes, this will remove/update existing attributes
     */
    PvdButton.prototype.setNativeAnchorAttributes = function (prevAttr) {
        var targetElement = this.querySelector('a');
        this.setNativeAttributes(this.pvdNativeAnchorAttributes, ANCHOR_PROTECTED_ATTR_MAP, targetElement, prevAttr);
    };
    // #endregion
    // #region LitElement lifecycles
    PvdButton.prototype.updated = function (changedProperties) {
        _super.prototype.updated.call(this, changedProperties);
        var prevAttr;
        if (this.pvdNativeButtonAttributes) {
            prevAttr = changedProperties.get('pvdNativeButtonAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeButtonAttributes(prevAttr);
        }
        if (this.pvdNativeAnchorAttributes) {
            prevAttr = changedProperties.get('pvdNativeAnchorAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeAnchorAttributes(prevAttr);
        }
    };
    PvdButton.prototype.renderButtonContents = function () {
        // unable to dynamically create an named s-slot (e.g., <s-slot name="icon-${position}"></s-slot> doesn't work)
        var leftIcon = this.renderLeftIcon();
        var rightIcon = this.renderRightIcon();
        var text = this.renderText();
        return html(templateObject_1 || (templateObject_1 = __makeTemplateObject(["<div class=\"pvd-button__contents\">", "", "", "</div>"], ["<div class=\"pvd-button__contents\">", "", "", "</div>"])), leftIcon, text, rightIcon);
    };
    PvdButton.prototype.renderText = function () {
        return this.hasText
            ? html(templateObject_2 || (templateObject_2 = __makeTemplateObject(["<span class=\"pvd-button__text\"><s-slot>", "</s-slot></span>"], ["<span class=\"pvd-button__text\"><s-slot>", "</s-slot></span>"])), this.pvdText) : html(templateObject_3 || (templateObject_3 = __makeTemplateObject(["<s-slot></s-slot>"], ["<s-slot></s-slot>"])));
    };
    PvdButton.prototype.renderLeftIcon = function () {
        return this.hasIconLeft
            ? html(templateObject_4 || (templateObject_4 = __makeTemplateObject(["<span class=\"pvd-button__icon pvd-button__icon--left\"\n          ><s-slot name=\"icon-left\">", "</s-slot></span\n        >"], ["<span class=\"pvd-button__icon pvd-button__icon--left\"\n          ><s-slot name=\"icon-left\">", "</s-slot></span\n        >"])), this.renderIcon(this.pvdIconLeft)) : html(templateObject_5 || (templateObject_5 = __makeTemplateObject([" <s-slot name=\"icon-left\"></s-slot>"], [" <s-slot name=\"icon-left\"></s-slot>"])));
    };
    PvdButton.prototype.renderRightIcon = function () {
        return this.hasIconRight
            ? html(templateObject_6 || (templateObject_6 = __makeTemplateObject(["<span class=\"pvd-button__icon pvd-button__icon--right\"\n          ><s-slot name=\"icon-right\">", "</s-slot></span\n        >"], ["<span class=\"pvd-button__icon pvd-button__icon--right\"\n          ><s-slot name=\"icon-right\">", "</s-slot></span\n        >"])), this.renderIcon(this.pvdIconRight)) : html(templateObject_7 || (templateObject_7 = __makeTemplateObject([" <s-slot name=\"icon-right\"></s-slot>"], [" <s-slot name=\"icon-right\"></s-slot>"])));
    };
    PvdButton.prototype.renderIcon = function (iconName) {
        if (!iconName) {
            return nothing;
        }
        return html(templateObject_8 || (templateObject_8 = __makeTemplateObject(["<pvd-scoped-icon\n      pvd-name=\"", "\"\n      pvd-size=\"", "\"\n    ></pvd-scoped-icon>"], ["<pvd-scoped-icon\n      pvd-name=\"", "\"\n      pvd-size=\"", "\"\n    ></pvd-scoped-icon>"])), iconName, this.pvdSize === StandardSize.large ? Size.medium : Size.small);
    };
    PvdButton.prototype.render = function () {
        var classMap = {
            // @ts-ignore
            default: this.constructor.namespace + "-button-root",
            modifierPrefix: 'pvd-button',
            modifiers: [
                {
                    condition: this.pvdIconOnly || (!this.hasText && this.hasIcon),
                    className: 'icon-only',
                },
                {
                    condition: this.pvdHref !== undefined,
                    className: 'anchor',
                },
                {
                    condition: this.pvdFullWidth,
                    className: 'full-width',
                },
                {
                    condition: this.pvdGradientBackground,
                    className: 'gradient-background',
                },
                {
                    condition: this.hasIcon,
                    className: 'with-icon',
                },
                {
                    condition: this.pvdSize !== StandardSize.medium,
                    className: "" + this.pvdSize,
                },
                {
                    condition: true,
                    className: this.pvdVariant,
                },
            ],
        };
        if (!this.pvdHref) {
            return html(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n        <button\n          ?disabled=", "\n          class=\"", "\"\n          id=\"", "\"\n          type=\"", "\"\n        >\n          ", "\n        </button>\n      "], ["\n        <button\n          ?disabled=", "\n          class=\"", "\"\n          id=\"", "\"\n          type=\"", "\"\n        >\n          ", "\n        </button>\n      "])), this.pvdDisabled, getClassName(classMap), ifDefined(this.pvdId), this.pvdType, this.renderButtonContents());
        }
        else {
            return html(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n        <a class=\"", "\" href=\"", "\" id=\"", "\">\n          ", "\n        </a>\n      "], ["\n        <a class=\"", "\" href=\"", "\" id=\"", "\">\n          ", "\n        </a>\n      "])), getClassName(classMap), this.pvdHref, ifDefined(this.pvdId), this.renderButtonContents());
        }
    };
    __decorate([
        property(_assign({ type: Boolean, reflect: true, attribute: 'pvd-disabled' }, PvdSlotifyBaseComponent.booleanAttribute('pvd-disabled'))),
        __metadata("design:type", Boolean)
    ], PvdButton.prototype, "pvdDisabled", void 0);
    __decorate([
        property(_assign({ type: Boolean, reflect: true, attribute: 'pvd-full-width' }, PvdSlotifyBaseComponent.booleanAttribute('pvd-full-width'))),
        __metadata("design:type", Boolean)
    ], PvdButton.prototype, "pvdFullWidth", void 0);
    __decorate([
        property(_assign({ type: Boolean, reflect: true, attribute: 'pvd-gradient-background' }, PvdSlotifyBaseComponent.booleanAttribute('pvd-gradient-background'))),
        __metadata("design:type", Boolean)
    ], PvdButton.prototype, "pvdGradientBackground", void 0);
    __decorate([
        property({ type: String, reflect: true, attribute: 'pvd-href' }),
        __metadata("design:type", String)
    ], PvdButton.prototype, "pvdHref", void 0);
    __decorate([
        property({ type: String, reflect: true, attribute: 'pvd-icon-right' }),
        __metadata("design:type", String)
    ], PvdButton.prototype, "pvdIconRight", void 0);
    __decorate([
        property({ type: String, reflect: true, attribute: 'pvd-icon-left' }),
        __metadata("design:type", String)
    ], PvdButton.prototype, "pvdIconLeft", void 0);
    __decorate([
        property(_assign({ type: Boolean, reflect: true, attribute: 'pvd-icon-only' }, PvdSlotifyBaseComponent.booleanAttribute('pvd-icon-only'))),
        __metadata("design:type", Boolean)
    ], PvdButton.prototype, "pvdIconOnly", void 0);
    __decorate([
        property({ type: String, reflect: true, attribute: 'pvd-id' }),
        __metadata("design:type", String)
    ], PvdButton.prototype, "pvdId", void 0);
    __decorate([
        property({ type: Object, attribute: 'pvd-native-anchor-attributes' }),
        __metadata("design:type", Object)
    ], PvdButton.prototype, "pvdNativeAnchorAttributes", void 0);
    __decorate([
        property({ type: Object, attribute: 'pvd-native-button-attributes' }),
        __metadata("design:type", Object)
    ], PvdButton.prototype, "pvdNativeButtonAttributes", void 0);
    __decorate([
        property(_assign({ type: String, reflect: true, attribute: 'pvd-size' }, PvdSlotifyBaseComponent.hasChanged(StandardSize))),
        __metadata("design:type", Number)
    ], PvdButton.prototype, "pvdSize", void 0);
    __decorate([
        property({ type: String, reflect: true, attribute: 'pvd-text' }),
        __metadata("design:type", String)
    ], PvdButton.prototype, "pvdText", void 0);
    __decorate([
        property(_assign({ type: String, reflect: true, attribute: 'pvd-type' }, PvdSlotifyBaseComponent.hasChanged(ButtonType))),
        __metadata("design:type", String)
    ], PvdButton.prototype, "pvdType", void 0);
    __decorate([
        property(_assign({ type: String, reflect: true, attribute: 'pvd-variant' }, PvdSlotifyBaseComponent.hasChanged(ButtonVariant))),
        __metadata("design:type", String)
    ], PvdButton.prototype, "pvdVariant", void 0);
    return PvdButton;
}(CustomElementMixin(ScopedElementsMixin(Slotify(PvdSlotifyBaseComponent)))));
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10;

export { ANCHOR_PROTECTED_ATTR_MAP, BUTTON_PROTECTED_ATTR_MAP, ButtonType, ButtonVariant, DEFAULT_DISABLED, DEFAULT_FULL_WIDTH, DEFAULT_GRADIENT_BACKGROUND, DEFAULT_ICON_ONLY, DEFAULT_SIZE, DEFAULT_TYPE, DEFAULT_VARIANT, PvdButton };
